import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { usePermisos } from '../../hooks/usePermisos';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  History, Search, FileDown, RefreshCw, Loader2, CheckCircle2, XCircle,
  Eye, AlertTriangle, Filter, Upload, Maximize2, Minimize2, User, Trash2,
} from 'lucide-react';

const fmtMoney = (n) =>
  (n || n === 0)
    ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n)
    : '—';

const fmtDate = (s) => (s ? String(s).slice(0, 19).replace('T', ' ') : '—');

const tierColor = (t) => (t === 'Alto' ? 'text-emerald-600' : t === 'Medio' ? 'text-amber-600' : t === 'Bajo' ? 'text-rose-600' : 'text-slate-400');

const DictamenBadge = ({ d }) =>
  d === 'Aprobar'
    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0"><CheckCircle2 className="h-3 w-3 mr-1" />Aprobar</Badge>
    : <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0"><XCircle className="h-3 w-3 mr-1" />Rechazar</Badge>;

const FUENTES = [
  { key: 'bcra', label: 'BCRA' },
  { key: 'pyp', label: 'PyP' },
  { key: 'nosis', label: 'Nosis' },
  { key: 'afip', label: 'AFIP' },
];

const Decisiones = () => {
  const { isAdmin } = usePermisos();
  const admin = isAdmin();
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fCuit, setFCuit] = useState('');
  const [fDictamen, setFDictamen] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [detLoading, setDetLoading] = useState(false);
  const [selFuentes, setSelFuentes] = useState([]);
  const [reconLoading, setReconLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // {id, cuit, nombre}
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (fCuit.replace(/[^0-9]/g, '')) params.cuit = fCuit.replace(/[^0-9]/g, '');
      if (fDictamen) params.dictamen = fDictamen;
      const r = await api.get('/motor/decisiones', { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [fCuit, fDictamen]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const abrirDetalle = async (id) => {
    setDetLoading(true);
    setDetalle({ id });
    setSelFuentes([]);
    setMaximized(false);
    try {
      const r = await api.get(`/motor/decisiones/${id}`);
      setDetalle(r.data);
    } catch (e) {
      toast.error('No se pudo abrir el detalle');
      setDetalle(null);
    } finally {
      setDetLoading(false);
    }
  };

  const descargarPDF = async (id, cuit) => {
    setPdfLoading(true);
    try {
      const r = await api.get(`/motor/decisiones/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluacion_${cuit}_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo generar el PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const toggleFuente = (key) => {
    setSelFuentes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const eliminar = async () => {
    if (!confirmDel?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/motor/decisiones/${confirmDel.id}`);
      toast.success('Consulta eliminada del historial');
      if (detalle?.id === confirmDel.id) setDetalle(null);
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar la consulta');
    } finally {
      setDeleting(false);
    }
  };

  const importar = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    try {
      const txt = await file.text();
      const parsed = JSON.parse(txt);
      const decisiones = Array.isArray(parsed) ? parsed : (parsed.decisiones || []);
      if (!Array.isArray(decisiones) || decisiones.length === 0) {
        toast.error('El archivo no tiene decisiones válidas'); return;
      }
      const r = await api.post('/motor/decisiones/import', { decisiones });
      const d = r.data;
      toast.success(`Importadas ${d.importadas} de ${d.recibidas}${d.errores?.length ? ` · ${d.errores.length} con error` : ''}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Archivo JSON inválido o error al importar');
    }
  };

  const reconsultar = async () => {
    if (!detalle?.id || selFuentes.length === 0) { toast.error('Elegí al menos una fuente'); return; }
    setReconLoading(true);
    try {
      const r = await api.post(`/motor/decisiones/${detalle.id}/reconsultar`, { fuentes: selFuentes });
      setDetalle(r.data);
      setSelFuentes([]);
      toast.success('Fuente(s) reconsultada(s) y decisión actualizada');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reconsultar');
    } finally {
      setReconLoading(false);
    }
  };

  const d = detalle;
  const bcra = d?.fuentes?.bcra || {};
  const pyp = d?.fuentes?.pyp || {};
  const nosis = d?.fuentes?.nosis || {};
  const afip = d?.fuentes?.afip || {};
  const resumenFuente = {
    bcra: bcra.ok ? `Situación ${bcra.sitMax ?? 0} · ${fmtMoney(bcra.total_deuda)} · ${bcra.total_entidades ?? 0} ent.` : (bcra.error || 'no consultada'),
    pyp: pyp.ok ? `${pyp.apenom || '—'} · edad ${pyp.edad ?? '—'} · ${fmtMoney(pyp.ingreso_estimado)}` : (pyp.error || 'no consultada'),
    nosis: nosis.ok ? `score ${nosis.score ?? '—'} · concursos ${nosis.concursos ?? '—'}` : (nosis.error || 'no consultada'),
    afip: afip.ok ? `${afip.razon_social || [afip.nombre, afip.apellido].filter(Boolean).join(' ') || '—'}` : (afip.error || 'no consultada'),
  };

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-6xl mx-auto" data-testid="decisiones">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><History className="h-6 w-6 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Decisiones</h1>
            <p className="text-sm text-slate-500">Historial de evaluaciones del motor ({total})</p>
          </div>
        </div>
        {admin && (
          <>
            <input ref={fileRef} type="file" accept="application/json,.json" onChange={importar} className="hidden" data-testid="dec-import-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="dec-import-btn">
              <Upload className="h-4 w-4 mr-2" /> Importar (JSON)
            </Button>
          </>
        )}
      </div>

      {/* Filtros */}
      <Card className="p-4 mb-6 border-slate-200 dark:border-slate-800">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Input value={fCuit} onChange={(e) => setFCuit(e.target.value)} placeholder="Filtrar por CUIT" className="sm:max-w-xs" data-testid="dec-filtro-cuit" />
          <select
            value={fDictamen}
            onChange={(e) => setFDictamen(e.target.value)}
            className="h-10 rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-3 text-sm"
            data-testid="dec-filtro-dictamen"
          >
            <option value="">Todos los dictámenes</option>
            <option value="Aprobar">Aprobar</option>
            <option value="Rechazar">Rechazar</option>
          </select>
          <Button variant="outline" onClick={load} data-testid="dec-filtrar-btn"><Filter className="h-4 w-4 mr-2" />Filtrar</Button>
        </div>
      </Card>

      {loading ? (
        <div className="text-center text-slate-500 py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-slate-400 py-16" data-testid="dec-empty">
          <History className="h-10 w-10 mx-auto mb-3 opacity-40" />
          No hay decisiones que coincidan.
        </div>
      ) : (
        <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Fecha</th>
                  <th className="text-left px-4 py-2.5 font-medium">Solicitante</th>
                  <th className="text-left px-4 py-2.5 font-medium">CUIT</th>
                  <th className="text-center px-4 py-2.5 font-medium">Dictamen</th>
                  <th className="text-center px-4 py-2.5 font-medium">Score</th>
                  <th className="text-right px-4 py-2.5 font-medium">Monto máx.</th>
                  <th className="text-left px-4 py-2.5 font-medium">Evaluado por</th>
                  <th className="text-right px-4 py-2.5 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`dec-row-${it.id}`}>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300 whitespace-nowrap">{fmtDate(it.evaluado_at)}{it.reconsultado_at && <span className="ml-1 text-[10px] text-teal-600">(reconsult.)</span>}</td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200 max-w-[180px] truncate" data-testid={`dec-nombre-${it.id}`}>{it.nombre || '—'}</td>
                    <td className="px-4 py-3 font-mono text-slate-700 dark:text-slate-200">{it.cuit}</td>
                    <td className="px-4 py-3 text-center"><DictamenBadge d={it.dictamen} /></td>
                    <td className={`px-4 py-3 text-center font-semibold ${tierColor(it.score_tier)}`}>{it.score ?? '—'}</td>
                    <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-200">{fmtMoney(it.estimacion?.monto_maximo)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[160px] truncate" data-testid={`dec-usuario-${it.id}`}>{it.user_email || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => abrirDetalle(it.id)} data-testid={`dec-ver-${it.id}`}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => descargarPDF(it.id, it.cuit)} data-testid={`dec-pdf-${it.id}`}><FileDown className="h-4 w-4" /></Button>
                        {admin && (
                          <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => setConfirmDel({ id: it.id, cuit: it.cuit, nombre: it.nombre })} data-testid={`dec-eliminar-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detalle */}
      <Dialog open={!!detalle} onOpenChange={(o) => { if (!o) setDetalle(null); }}>
        <DialogContent className={maximized ? 'max-w-[96vw] w-[96vw] max-h-[95vh] overflow-y-auto' : 'max-w-2xl max-h-[90vh] overflow-y-auto'} data-testid="dec-detalle">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle>Detalle de evaluación</DialogTitle>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setMaximized((m) => !m)} data-testid="dec-maximizar-btn" title={maximized ? 'Restaurar' : 'Pantalla completa'}>
                {maximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>
            <DialogDescription className="text-xs text-slate-400">Resultado del Motor de Decisión con el desglose por fuente y reglas aplicadas.</DialogDescription>
          </DialogHeader>

          {detLoading || !d?.cuit ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  {d.nombre && <p className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-tight">{d.nombre}</p>}
                  <p className="text-xs text-slate-500">CUIT</p>
                  <p className="font-mono font-semibold text-slate-800 dark:text-slate-100">{d.cuit}</p>
                  <p className="text-xs text-slate-400">{fmtDate(d.evaluado_at)}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><User className="h-3 w-3" /> Evaluado por: {d.user_email || '—'}</p>
                </div>
                <div className="text-right">
                  <DictamenBadge d={d.dictamen} />
                  <p className={`text-2xl font-bold mt-1 ${tierColor(d.score_tier)}`}>{d.score ?? '—'} <span className="text-xs font-normal">{d.score_tier}</span></p>
                </div>
              </div>

              {d.motivos?.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                  <p className="text-xs font-semibold text-rose-700 mb-1">Motivos de rechazo</p>
                  {d.motivos.map((m, i) => (
                    <p key={i} className="text-sm text-rose-700 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{m}</p>
                  ))}
                </div>
              )}

              {d.dictamen === 'Aprobar' && d.estimacion?.monto_maximo > 0 && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2"><p className="text-xs text-slate-500">Monto máx.</p><p className="font-semibold text-slate-800 dark:text-slate-100">{fmtMoney(d.estimacion.monto_maximo)}</p></div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2"><p className="text-xs text-slate-500">Cuota</p><p className="font-semibold text-slate-800 dark:text-slate-100">{fmtMoney(d.estimacion.cuota_promedio)}</p></div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2"><p className="text-xs text-slate-500">Ingreso</p><p className="font-semibold text-slate-800 dark:text-slate-100">{fmtMoney(d.estimacion.ingreso_base)}</p></div>
                </div>
              )}

              {/* Fuentes + reconsultar */}
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Fuentes (marcá para reconsultar)</p>
                <div className="space-y-2">
                  {FUENTES.map((f) => {
                    const fu = d.fuentes?.[f.key] || {};
                    return (
                      <label key={f.key} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-2.5 cursor-pointer" data-testid={`dec-fuente-${f.key}`}>
                        <Checkbox checked={selFuentes.includes(f.key)} onCheckedChange={() => toggleFuente(f.key)} data-testid={`dec-check-${f.key}`} />
                        <span className={`h-2 w-2 rounded-full ${fu.ok ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <span className="w-14 font-medium text-slate-700 dark:text-slate-200">{f.label}</span>
                        <span className="flex-1 text-xs text-slate-500 truncate">{resumenFuente[f.key]}</span>
                        {fu._cache && <span className="text-[10px] text-slate-400">caché</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <Button variant="outline" size="sm" onClick={reconsultar} disabled={reconLoading || selFuentes.length === 0} data-testid="dec-reconsultar-btn">
                  {reconLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Reconsultar{selFuentes.length ? ` (${selFuentes.length})` : ''}
                </Button>
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => descargarPDF(d.id, d.cuit)} disabled={pdfLoading} data-testid="dec-pdf-btn">
                  {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                  Descargar PDF
                </Button>
              </div>
              {d.reconsultado_at && <p className="text-[11px] text-slate-400 text-right">Última reconsulta: {fmtDate(d.reconsultado_at)}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmación de eliminación (solo admin) */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o && !deleting) setConfirmDel(null); }}>
        <DialogContent className="max-w-md" data-testid="dec-eliminar-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-500" /> Eliminar consulta</DialogTitle>
            <DialogDescription>
              Vas a eliminar del historial la evaluación de <strong>{confirmDel?.nombre || confirmDel?.cuit}</strong> (CUIT {confirmDel?.cuit}). Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirmDel(null)} disabled={deleting} data-testid="dec-eliminar-cancel">Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={eliminar} disabled={deleting} data-testid="dec-eliminar-confirm">
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Decisiones;
